import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Loader2, Plus } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loading, EmptyState } from "@/components/common/Loading";
import { useAuth } from "@/context/AuthContext";
import { useRealtime, useRealtimeEvent } from "@/context/RealtimeContext";
import { ChatMessageText } from "@/components/social/ChatMessageText";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { ReactionBar } from "@/components/social/ReactionBar";
import { AuthorLink } from "@/components/social/AuthorLink";
import { OfficialBadge } from "@/components/social/OfficialBadge";
import { MessageAvatar } from "@/components/social/MessageAvatar";
import { applyReactionToggle } from "@/lib/reactions";
import { timeAgo, fullTime, messageStamp } from "@/lib/timeago";
import { cn } from "@/lib/utils";

function NewChatButton({ onStarted }) {
  const [open, setOpen] = useState(false);
  const [connections, setConnections] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setConnections(null);
    try { const { data } = await api.get("/network/connections"); setConnections(data); }
    catch (e) { setConnections([]); }
  };

  const start = async (c) => {
    setBusyId(c.user_id);
    try {
      const { data } = await api.post("/messages/conversations", { target_user_id: c.user_id });
      setOpen(false); setQ("");
      onStarted(data.id);
    } catch (e) { toast.error("Could not start chat"); }
    finally { setBusyId(null); }
  };

  const filtered = (connections || []).filter((c) =>
    (c.display_name || "").toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <Button onClick={() => { setOpen(true); load(); }} className="shrink-0" data-testid="new-chat-button">
        <Plus className="mr-1.5 h-4 w-4" /> New chat
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-chat-dialog">
          <DialogHeader>
            <DialogTitle>New message</DialogTitle>
            <DialogDescription>Pick a connection to start a conversation.</DialogDescription>
          </DialogHeader>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search connections…" data-testid="new-chat-search" />
          <ScrollArea className="max-h-72">
            {connections === null ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="new-chat-empty">
                {(connections.length === 0)
                  ? "You have no connections yet. Connect with members to start chatting."
                  : "No matching connections."}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((c) => (
                  <li key={c.user_id}>
                    <button
                      onClick={() => start(c)}
                      disabled={busyId === c.user_id}
                      className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition-colors hover:bg-secondary/60 disabled:opacity-60"
                      data-testid={`new-chat-connection-${c.user_id}`}
                    >
                      <Avatar className="h-9 w-9">
                        {c.headshot_url && <AvatarImage src={fileUrl(c.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-teal-50 text-teal-700">{(c.display_name || "?")[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-[600] text-foreground">{c.display_name || "Member"}</div>
                        {c.headline && <div className="truncate text-xs text-muted-foreground">{c.headline}</div>}
                      </div>
                      {busyId === c.user_id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const { reloadMessageUnread } = useRealtime();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null); // {id, other}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [readOnly, setReadOnly] = useState(null);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  const inputRef = useRef(null);
  activeRef.current = active;

  const loadConvos = async () => {
    try { const { data } = await api.get("/messages/conversations"); setConvos(data); }
    catch (e) { setConvos([]); }
  };
  useEffect(() => { loadConvos(); }, []);

  const openConvo = async (id) => {
    if (!id) return;
    setParams({ c: id });
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/messages/conversations/${id}/messages`);
      setActive({ id, other: data.other });
      // Platform User Blocking V1 — a blocked thread stays fully readable but accepts no
      // new replies. The notice never says who blocked whom.
      setReadOnly(data.can_send === false ? (data.why_text || "You can't reply to this conversation.") : null);
      setMessages(data.messages || []);
      if (reloadMessageUnread) reloadMessageUnread();
      loadConvos();
    } catch (e) { toast.error("Could not open conversation"); }
    finally { setLoadingThread(false); }
  };
  useEffect(() => { if (activeId) openConvo(activeId); /* eslint-disable-next-line */ }, [activeId]);

  useRealtimeEvent((evt) => {
    if (evt.type === "message") {
      const cur = activeRef.current;
      if (cur && evt.data.conversation_id === cur.id) {
        setMessages((prev) => (prev.find((m) => m.id === evt.data.message.id) ? prev : [...prev, evt.data.message]));
        api.post(`/messages/conversations/${cur.id}/read`).catch(() => {});
        if (reloadMessageUnread) reloadMessageUnread();
      }
      loadConvos();
    }
    if (evt.type === "message_reaction") {
      const cur = activeRef.current;
      if (cur && evt.data.conversation_id === cur.id) {
        setMessages((prev) => prev.map((m) => (m.id === evt.data.message_id ? { ...m, reactions: evt.data.reactions } : m)));
      }
    }
    // Platform User Blocking V1 — re-read the open thread so its read-only state applies
    // immediately, without a reload or a fresh sign-in.
    if (evt.type === "relationship_state_changed") {
      const cur = activeRef.current;
      if (cur) openConvo(cur.id);
      loadConvos();
    }
  });

  const react = async (messageId, key) => {
    if (!active) return;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? applyReactionToggle(m, key) : m)));
    try { await api.post(`/messages/conversations/${active.id}/messages/${messageId}/react`, { emoji: key }); }
    catch (e) { /* realtime/refetch reconciles */ }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text.trim();
    setText("");
    setSending(true);
    try {
      const { data } = await api.post(`/messages/conversations/${active.id}`, { body });
      setMessages((prev) => [...prev, data]);
      loadConvos();
    } catch (e) { toast.error("Could not send"); setText(body); }
    finally { setSending(false); }
  };

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // The Charla bubble covers the Send button on phones — park it while Messages is open.
  useEffect(() => {
    document.body.classList.add("hide-charla-mobile");
    return () => document.body.classList.remove("hide-charla-mobile");
  }, []);

  if (convos === null) return <Loading />;

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Messages</h1>
          <p className="mt-1 text-muted-foreground">Real-time conversations with your network.</p>
        </div>
        <NewChatButton onStarted={openConvo} />
      </div>

      <Card className="mt-6 grid h-[64vh] grid-cols-1 grid-rows-1 overflow-hidden lg:grid-cols-12">
        {/* Conversation list */}
        <div className={cn("min-h-0 overflow-hidden border-border lg:col-span-4 lg:border-r", active && "hidden lg:block")}>
          <ScrollArea className="h-full">
            {convos.length === 0 ? (
              <div className="p-6"><EmptyState icon={MessageSquare} title="No conversations" description="Start a chat from any member's profile." testId="messages-empty" /></div>
            ) : (
              <ul className="divide-y divide-border">
                {convos.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => openConvo(c.id)}
                      className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60", active && active.id === c.id && "bg-teal-50/60")}
                      data-testid={`convo-${c.id}`}
                    >
                      <Avatar className="h-11 w-11">
                        {c.other && c.other.headshot_url && <AvatarImage src={fileUrl(c.other.headshot_url)} alt="" />}
                        <AvatarFallback className="bg-teal-50 text-teal-700">{(c.other && c.other.display_name ? c.other.display_name[0] : "?")}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-[600] text-foreground">{c.other ? c.other.display_name : "Member"}</span>
                          {c.other && c.other.official && <OfficialBadge label="Official" testId={`convo-official-${c.id}`} />}
                          {c.last_message_at && <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(c.last_message_at)}</span>}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm text-muted-foreground">{c.last_message || "No messages yet"}</span>
                          {c.unread > 0 && <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground" data-testid={`convo-unread-${c.id}`}>{c.unread}</span>}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </div>

        {/* Thread */}
        <div className={cn("flex min-h-0 flex-col overflow-hidden lg:col-span-8", !active && "hidden lg:flex")}>
          {!active ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <EmptyState icon={MessageSquare} title="Select a conversation" description="Choose a conversation to start chatting." />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <button className="lg:hidden" onClick={() => { setActive(null); setParams({}); }} aria-label="Back" data-testid="thread-back"><ArrowLeft className="h-5 w-5" /></button>
                <Avatar className="h-9 w-9">
                  {active.other && active.other.headshot_url && <AvatarImage src={fileUrl(active.other.headshot_url)} alt="" />}
                  <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{(active.other && active.other.display_name ? active.other.display_name[0] : "?")}</AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-2 font-[620] text-foreground" data-testid="thread-title">
                  <AuthorLink card={active.other} className="text-foreground hover:underline" fallback="Member" testId="thread-title-link" />
                  {active.other && active.other.official && <OfficialBadge testId="thread-official-badge" />}
                </div>
              </div>

              <ScrollArea className="min-h-0 flex-1 px-4 py-4">
                {loadingThread ? <Loading /> : (
                  <div className="space-y-2.5">
                    {messages.map((m) => {
                      const mine = m.sender_id === (user && user.user_id);
                      return (
                        <div key={m.id} className={cn("group flex items-end gap-2", mine ? "flex-row-reverse" : "flex-row")} data-testid={`msg-${m.id}`}>
                          {/* The sender's face beside their message, linking to their
                              profile (shared with the chat dock and Group Member
                              Messages). */}
                          <MessageAvatar card={active.other} mine={mine} size="h-8 w-8"
                                         testId={`msg-avatar-${m.id}`} />
                          <div className={cn("flex min-w-0 flex-col", mine ? "items-end" : "items-start")}>
                          <div className={cn("max-w-[75%] rounded-2xl px-3.5 py-2 text-[14px] leading-6", mine ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-secondary text-foreground")} title={fullTime(m.created_at)}>
                            <ChatMessageText body={m.body} />
                          </div>
                          <div className={cn("mt-0.5", (!m.reactions || Object.keys(m.reactions).length === 0) && "opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100")}>
                            <ReactionBar reactions={m.reactions} myReaction={m.my_reaction} onReact={(k) => react(m.id, k)} testId={`msg-react-${m.id}`} />
                          </div>
                          <span className="px-1 text-[10px] text-muted-foreground" data-testid={`msg-time-${m.id}`}>{messageStamp(m.created_at, user && user.timezone)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                )}
              </ScrollArea>

              {readOnly ? (
                <div className="border-t border-border p-3 text-center text-sm text-muted-foreground" data-testid="message-readonly-notice">
                  {readOnly}
                </div>
              ) : (
              <div className="flex items-center gap-2 border-t border-border p-3">
                <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText} testId="message-emoji-button" />
                <Input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder="Type a message…"
                  data-testid="message-input"
                />
                <Button onClick={send} disabled={sending || !text.trim()} data-testid="message-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
